import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2, Home, FileText, Calendar } from 'lucide-react';
import { paymentsAPI, bookingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { generateInvoicePDF } from '../utils/invoiceGenerator';

const BookingSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthToken, isAuthenticated } = useAuth();
  
  const [status, setStatus] = useState('loading'); // loading, success, pending, failed
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login', { replace: true });
      return;
    }

    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('failed');
      setError('No session ID provided');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const token = getAuthToken();
      const result = await paymentsAPI.getPaymentStatus(sessionId, token);
      
      if (result.paymentStatus === 'paid') {
        setStatus(result.webhookVerified ? 'success' : 'pending');
        
        // Fetch booking details
        if (bookingId) {
          try {
            const bookingData = await bookingAPI.getBooking(bookingId);
            setBooking(bookingData);
          } catch (e) {
            console.error('Error fetching booking:', e);
          }
        } else if (result.booking?._id) {
          try {
            const bookingData = await bookingAPI.getBooking(result.booking._id);
            setBooking(bookingData);
          } catch (e) {
            console.error('Error fetching booking:', e);
          }
        }
      } else if (result.status === 'expired') {
        setStatus('failed');
        setError('Payment session expired');
      } else {
        // Payment not complete, maybe still processing
        setStatus('pending');
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setStatus('failed');
      setError(err.response?.data?.detail || 'Unable to verify payment');
    }
  };

  const handleDownloadInvoice = () => {
    if (booking) {
      generateInvoicePDF(booking);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-xl" data-testid="booking-success-card">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <div className="py-12">
              <Loader2 size={64} className="mx-auto text-[#1F3A68] animate-spin mb-4" />
              <h2 className="text-2xl font-bold text-gray-700 mb-2">Verifying Payment...</h2>
              <p className="text-gray-600">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-8">
              <CheckCircle2 size={80} className="mx-auto text-green-500 mb-6" />
              <h2 className="text-3xl font-bold text-[#1F3A68] mb-4" data-testid="booking-confirmed-title">
                Booking Confirmed!
              </h2>
              <p className="text-gray-600 mb-6">
                Your payment was successful and your booking is now confirmed.
              </p>
              
              {booking && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-semibold text-lg mb-2">{booking.propertyTitle}</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><span className="font-medium">Booking Ref:</span> {booking.bookingRef}</p>
                    <p><span className="font-medium">Check-in:</span> {booking.checkIn}</p>
                    <p><span className="font-medium">Check-out:</span> {booking.checkOut}</p>
                    <p><span className="font-medium">Total Paid:</span> £{booking.pricing?.total?.toFixed(2)}</p>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col gap-3">
                {booking && booking.status === 'confirmed' && (
                  <Button 
                    variant="outline" 
                    onClick={handleDownloadInvoice}
                    className="w-full"
                    data-testid="download-invoice-btn"
                  >
                    <FileText size={16} className="mr-2" />
                    Download Invoice
                  </Button>
                )}
                <Button 
                  onClick={() => navigate('/guest/browse?tab=upcoming')}
                  className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                  data-testid="view-bookings-btn"
                >
                  <Calendar size={16} className="mr-2" />
                  View My Bookings
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  <Home size={16} className="mr-2" />
                  Back to Home
                </Button>
              </div>
            </div>
          )}

          {status === 'pending' && (
            <div className="py-8">
              <Loader2 size={64} className="mx-auto text-amber-500 animate-spin mb-6" />
              <h2 className="text-2xl font-bold text-gray-700 mb-4">Payment Processing</h2>
              <p className="text-gray-600 mb-6">
                Your payment is being processed. This may take a few moments.
                Your booking will be confirmed once payment is verified.
              </p>
              <Button 
                onClick={checkPaymentStatus}
                variant="outline"
                className="mr-3"
              >
                Check Status
              </Button>
              <Button 
                onClick={() => navigate('/guest/browse?tab=upcoming')}
                className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
              >
                View My Bookings
              </Button>
            </div>
          )}

          {status === 'failed' && (
            <div className="py-8">
              <XCircle size={64} className="mx-auto text-red-500 mb-6" />
              <h2 className="text-2xl font-bold text-gray-700 mb-4">Payment Verification Failed</h2>
              <p className="text-gray-600 mb-6">
                {error || 'We could not verify your payment. Please check your bookings or try again.'}
              </p>
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate('/guest/browse?tab=upcoming')}
                  className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                >
                  View My Bookings
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingSuccessPage;
